import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  Calendar,
  Users,
  Filter,
  PieChart as PieIcon,
  Layers
} from 'lucide-react';

const CATEGORY_COLORS = {
  'general': '#a0b2c6',
  'movies': '#b388ff',
  'entertainment': '#b388ff',
  'dining out': '#81c784',
  'food': '#81c784',
  'restaurant': '#81c784',
  'groceries': '#66bb6a',
  'supermarket': '#66bb6a',
  'gas/fuel': '#ff8a80',
  'fuel': '#ff8a80',
  'gas': '#ff8a80',
  'transport': '#4fc3f7',
  'shopping': '#ffd54f',
  'bills': '#80cbc4',
  'utilities': '#80cbc4',
  'travel': '#ffb74d',
  'other': '#90a4ae'
};

const DEFAULT_COLOR_PALETTE = [
  '#a0b2c6',
  '#b388ff',
  '#81c784',
  '#66bb6a',
  '#ff8a80',
  '#ffd54f',
  '#4fc3f7',
  '#ffb74d',
  '#80cbc4',
  '#ce93d8'
];

function getCatColor(name, index = 0) {
  const norm = (name || '').toLowerCase().trim();
  if (CATEGORY_COLORS[norm]) return CATEGORY_COLORS[norm];
  for (const [key, color] of Object.entries(CATEGORY_COLORS)) {
    if (norm.includes(key)) return color;
  }
  return DEFAULT_COLOR_PALETTE[index % DEFAULT_COLOR_PALETTE.length];
}

export default function GroupChartsView({
  activeGroup,
  groupMembers = [],
  transactions = [],
  currency = 'DH',
  formatMoney,
  onBack
}) {
  const [timeframe, setTimeframe] = useState('ALL'); // ALL, THIS_MONTH, LAST_MONTH, THIS_YEAR
  const [selectedMemberId, setSelectedMemberId] = useState('ALL');
  const [metric, setMetric] = useState('CATEGORY'); // CATEGORY, MEMBER
  const [hoveredSlice, setHoveredSlice] = useState(null);

  // Filter transactions by group, timeframe, and member
  const filteredTxs = useMemo(() => {
    const groupTxs = transactions.filter(tx => tx.group_id === activeGroup?.group_id);
    const now = new Date();

    return groupTxs.filter(tx => {
      const txDate = tx.date ? new Date(tx.date) : new Date();

      // Timeframe filter
      if (timeframe === 'THIS_MONTH') {
        if (txDate.getFullYear() !== now.getFullYear() || txDate.getMonth() !== now.getMonth()) {
          return false;
        }
      } else if (timeframe === 'LAST_MONTH') {
        const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        if (txDate.getFullYear() !== lastMonthYear || txDate.getMonth() !== lastMonth) {
          return false;
        }
      } else if (timeframe === 'THIS_YEAR') {
        if (txDate.getFullYear() !== now.getFullYear()) {
          return false;
        }
      }

      // Member filter
      if (selectedMemberId !== 'ALL') {
        const payerId = tx.user_id || tx.paid_by_user_id;
        const isSplitMember = tx.splits && tx.splits.some(s => s.member_user_id === selectedMemberId);
        if (payerId !== selectedMemberId && !isSplitMember) {
          return false;
        }
      }

      return true;
    });
  }, [transactions, activeGroup, timeframe, selectedMemberId]);

  // Aggregate category spending totals
  const categoryBreakdown = useMemo(() => {
    const map = {};
    let totalSpend = 0;

    filteredTxs.forEach(tx => {
      let amt = parseFloat(tx.total_amount || 0);

      // If specific member is filtered, compute their exact share/owed portion
      if (selectedMemberId !== 'ALL') {
        const userSplit = tx.splits?.find(s => s.member_user_id === selectedMemberId);
        amt = userSplit ? parseFloat(userSplit.owed_amount || 0) : 0;
      }

      if (amt <= 0) return;

      const catName = tx.category_name || 'General';
      map[catName] = (map[catName] || 0) + amt;
      totalSpend += amt;
    });

    const entries = Object.entries(map).map(([name, amount], idx) => ({
      name,
      amount,
      percentage: totalSpend > 0 ? (amount / totalSpend) * 100 : 0,
      color: getCatColor(name, idx)
    }));

    // Sort descending by amount
    entries.sort((a, b) => b.amount - a.amount);
    return { list: entries, total: totalSpend };
  }, [filteredTxs, selectedMemberId]);

  // SVG Pie/Donut Chart Slice Calculations
  const chartSlices = useMemo(() => {
    const { list, total } = categoryBreakdown;
    if (total <= 0 || list.length === 0) return [];

    let accumulatedAngle = 0;
    return list.map(item => {
      const angle = (item.amount / total) * 360;
      const startAngle = accumulatedAngle;
      const endAngle = accumulatedAngle + angle;
      accumulatedAngle += angle;

      // Coordinate calculations for SVG arc
      const radius = 90;
      const innerRadius = 45; // donut hole
      const cx = 100;
      const cy = 100;

      const startRad = (startAngle - 90) * (Math.PI / 180);
      const endRad = (endAngle - 90) * (Math.PI / 180);

      const x1 = cx + radius * Math.cos(startRad);
      const y1 = cy + radius * Math.sin(startRad);
      const x2 = cx + radius * Math.cos(endRad);
      const y2 = cy + radius * Math.sin(endRad);

      const x1Inner = cx + innerRadius * Math.cos(startRad);
      const y1Inner = cy + innerRadius * Math.sin(startRad);
      const x2Inner = cx + innerRadius * Math.cos(endRad);
      const y2Inner = cy + innerRadius * Math.sin(endRad);

      const largeArc = angle > 180 ? 1 : 0;

      const pathData = list.length === 1
        ? `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 0 ${cx} ${cy + radius} A ${radius} ${radius} 0 1 0 ${cx} ${cy - radius} M ${cx} ${cy - innerRadius} A ${innerRadius} ${innerRadius} 0 1 1 ${cx} ${cy + innerRadius} A ${innerRadius} ${innerRadius} 0 1 1 ${cx} ${cy - innerRadius} Z`
        : `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x2Inner} ${y2Inner} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1Inner} ${y1Inner} Z`;

      return {
        ...item,
        pathData,
        startAngle,
        endAngle
      };
    });
  }, [categoryBreakdown]);

  return (
    <div className="group-charts-view animate-fade-in" style={{
      minHeight: '100%',
      paddingBottom: '80px'
    }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px 20px',
        background: 'rgba(18, 24, 38, 0.9)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border-glass)',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid var(--border-glass)',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-main)',
            cursor: 'pointer'
          }}
          title="Back to Group"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Charts</h2>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {activeGroup?.group_name || 'Group'} • Spending Insights
          </span>
        </div>
      </div>

      {/* Main Container */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
        
        {/* Filter Pills (Matching Sample Image 3) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '400px', alignItems: 'center' }}>
          {/* Group Name Pill */}
          <div style={{
            padding: '8px 20px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-glass)',
            fontSize: '14px',
            fontWeight: '700',
            color: 'var(--text-main)',
            width: '100%',
            textAlign: 'center',
            boxSizing: 'border-box'
          }}>
            {activeGroup?.group_name || 'Group'}
          </div>

          {/* Metric Selector Pill */}
          <div style={{
            padding: '8px 20px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--border-glass)',
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-muted)',
            width: '100%',
            textAlign: 'center',
            boxSizing: 'border-box'
          }}>
            Spending by category
          </div>

          {/* Timeframe Filter Pill */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px'
          }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Showing expenses for:</span>
            <select
              value={timeframe}
              onChange={e => setTimeframe(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All time</option>
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>

        {/* Dynamic SVG Pie / Donut Chart */}
        <div style={{ position: 'relative', width: '220px', height: '220px', margin: '10px 0' }}>
          {categoryBreakdown.total > 0 ? (
            <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              {chartSlices.map((slice, sIdx) => {
                const isHovered = hoveredSlice?.name === slice.name;
                return (
                  <path
                    key={sIdx}
                    d={slice.pathData}
                    fill={slice.color}
                    stroke="rgba(18, 24, 38, 0.8)"
                    strokeWidth="1.5"
                    style={{
                      cursor: 'pointer',
                      transition: 'opacity 0.2s, transform 0.2s',
                      opacity: hoveredSlice ? (isHovered ? 1 : 0.4) : 0.95
                    }}
                    onMouseEnter={() => setHoveredSlice(slice)}
                    onMouseLeave={() => setHoveredSlice(null)}
                  />
                );
              })}
            </svg>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '4px dashed rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: 'var(--text-muted)',
              fontSize: '12px'
            }}>
              No data
            </div>
          )}

          {/* Center Info in Donut Hole */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            {hoveredSlice ? (
              <>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold' }}>{hoveredSlice.name}</div>
                <div style={{ fontSize: '13px', fontWeight: '800', color: hoveredSlice.color }}>
                  {hoveredSlice.percentage.toFixed(1)}%
                </div>
              </>
            ) : (
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600' }}>
                {categoryBreakdown.list.length} categories
              </div>
            )}
          </div>
        </div>

        {/* Member Filter Pill */}
        <div style={{ width: '100%', maxWidth: '400px', display: 'flex', justifyContent: 'center' }}>
          <select
            value={selectedMemberId}
            onChange={e => setSelectedMemberId(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-glass)',
              color: 'var(--text-main)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'center'
            }}
          >
            <option value="ALL">All group members</option>
            {groupMembers.map(m => (
              <option key={m.user_id} value={m.user_id}>
                {m.display_name || m.nickname || m.email?.split('@')[0]}
              </option>
            ))}
          </select>
        </div>

        {/* Category Breakdown Table (Matching Sample Image 3) */}
        <div style={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Table Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'var(--text-main)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <span>Category</span>
            <span>Total</span>
          </div>

          {/* Category Rows */}
          {categoryBreakdown.list.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', padding: '16px 0' }}>
              No expenses for the selected timeframe.
            </p>
          ) : (
            categoryBreakdown.list.map((cat, idx) => (
              <div
                key={idx}
                onMouseEnter={() => setHoveredSlice(cat)}
                onMouseLeave={() => setHoveredSlice(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  background: hoveredSlice?.name === cat.name ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                  transition: 'background 0.15s ease',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '3px',
                    background: cat.color,
                    flexShrink: 0
                  }} />
                  <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: '500' }}>
                    {cat.name}
                  </span>
                </div>

                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>
                  {formatMoney(cat.amount, currency)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
